import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, Timestamp, doc, getDoc } from 'firebase/firestore';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { db } from '../firebase';
import { useNotification } from '../contexts/NotificationContext';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface Contact {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  company?: string;
  status: 'subscribed' | 'unsubscribed' | 'pending' | 'bounced';
  tags?: string[];
  customFields?: { [key: string]: any };
  addedAt: Timestamp;
  lastChanged?: Timestamp;
  userId: string;
  listId?: string;
  twoFactorEnabled?: boolean;
}

interface UserInfo {
  id: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
}

const UserContacts: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'email' | 'createdAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (userId) {
      loadUserInfo();
      loadUserContacts();
    }
  }, [userId]);

  // Close the export menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUserInfo = async () => {
    try {
      const userDocRef = doc(db, 'users', userId!);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('User data loaded:', userData);
        setUserInfo({
          id: userId!,
          email: userData.email || 'Unknown',
          displayName: userData.displayName,
          firstName: userData.firstName,
          lastName: userData.lastName,
          companyName: userData.companyName
        });
      } else {
        console.log('No user found with ID:', userId);
        setUserInfo({
          id: userId!,
          email: 'Unknown User',
          displayName: 'Unknown',
          firstName: '',
          lastName: '',
          companyName: ''
        });
      }
    } catch (error) {
      console.error('Error loading user info:', error);
      showError('Failed to load user information');
    }
  };

  const loadUserContacts = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      console.log('Loading contacts for userId:', userId);

      const contactsRef = collection(db, 'contacts');

      // Try the ordered query first
      let querySnapshot;
      try {
        const q = query(
          contactsRef,
          where('userId', '==', userId),
          orderBy('addedAt', 'desc')
        );
        querySnapshot = await getDocs(q);
      } catch (orderError) {
        console.log('Ordered query failed, trying without orderBy:', orderError);
        // Fallback to query without orderBy if the compound query fails
        const q = query(
          contactsRef,
          where('userId', '==', userId)
        );
        querySnapshot = await getDocs(q);
      }

      console.log('Query snapshot size:', querySnapshot.size);
      console.log('Query snapshot docs:', querySnapshot.docs);

      const contactsData: Contact[] = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Contact doc data:', doc.id, data);
        return {
          id: doc.id,
          ...data
        };
      }) as Contact[];

      // Sort manually if we couldn't use orderBy
      contactsData.sort((a, b) => {
        const aTime = a.addedAt?.toMillis() || 0;
        const bTime = b.addedAt?.toMillis() || 0;
        return bTime - aTime; // Descending order
      });

      console.log('Processed contacts data:', contactsData);
      setContacts(contactsData);
    } catch (error) {
      console.error('Error loading user contacts:', error);
      console.error('Error details:', {
        userId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined
      });
      showError('Failed to load user contacts');
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return contacts.filter(contact => {
      const matchesSearch =
        !term ||
        contact.email.toLowerCase().includes(term) ||
        (contact.firstName && contact.firstName.toLowerCase().includes(term)) ||
        (contact.lastName && contact.lastName.toLowerCase().includes(term)) ||
        (contact.company && contact.company.toLowerCase().includes(term));

      const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [contacts, searchTerm, statusFilter]);

  const sortedContacts = useMemo(() => {
    return [...filteredContacts].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name': {
          const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email;
          const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email;
          comparison = aName.localeCompare(bName);
          break;
        }
        case 'email':
          comparison = a.email.localeCompare(b.email);
          break;
        case 'createdAt':
          comparison = (a.addedAt?.toMillis() || 0) - (b.addedAt?.toMillis() || 0);
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [filteredContacts, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedContacts.length / pageSize));
  const indexOfFirstContact = (currentPage - 1) * pageSize;
  const indexOfLastContact = indexOfFirstContact + pageSize;
  const paginatedContacts = useMemo(
    () => sortedContacts.slice(indexOfFirstContact, indexOfLastContact),
    [sortedContacts, indexOfFirstContact, indexOfLastContact]
  );

  // Keep the current page inside range when filters, sorting or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortBy, sortOrder, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Page numbers to render: first, last, current and its neighbours
  const visiblePages = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1).filter(
      page =>
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 1 && page <= currentPage + 1)
    );
  }, [totalPages, currentPage]);

  /**
   * Build the flat rows an export needs. Admin exports are generated entirely in
   * the browser and deliberately write nothing back to Firestore, so viewing a
   * user's contacts never leaves a record in that user's export history.
   */
  const buildExportRows = (rows: Contact[]) =>
    rows.map(contact => ({
      'First Name': contact.firstName || '',
      'Last Name': contact.lastName || '',
      Email: contact.email,
      Phone: contact.phone || '',
      Company: contact.company || '',
      Status: contact.status || '',
      Tags: contact.tags?.join(', ') || '',
      Added: formatDate(contact.addedAt),
      'Last Activity': contact.lastChanged ? formatDate(contact.lastChanged) : ''
    }));

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportFileBaseName = () => {
    const who =
      userInfo?.email?.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '') || userId || 'user';
    return `scubamail-contacts-${who}-${new Date().toISOString().split('T')[0]}`;
  };

  const handleExport = async (format: 'CSV' | 'Excel', scope: 'all' | 'filtered') => {
    const rows = scope === 'all' ? contacts : sortedContacts;

    setShowExportMenu(false);

    if (rows.length === 0) {
      showError('There are no contacts to export.');
      return;
    }

    setIsExporting(true);

    try {
      const data = buildExportRows(rows);
      const baseName = exportFileBaseName();

      if (format === 'CSV') {
        // Papa handles quoting/escaping for values containing commas or quotes.
        const csv = Papa.unparse(data);
        downloadBlob(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }), `${baseName}.csv`);
      } else {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');
        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        downloadBlob(
          new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }),
          `${baseName}.xlsx`
        );
      }

      showSuccess(`Exported ${rows.length} contact${rows.length === 1 ? '' : 's'} as ${format}.`);
    } catch (error) {
      console.error('Error exporting user contacts:', error);
      showError('Failed to export contacts. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'subscribed': return 'bg-green-100 text-green-800';
      case 'unsubscribed': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-blue-100 text-blue-800';
      case 'bounced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp: Timestamp | undefined) => {
    if (!timestamp) return 'Unknown';
    try {
      return timestamp.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/admin/control-panel-7x9k2m/overview')}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {userInfo ? `${userInfo.displayName || userInfo.firstName || 'User'}'s Contacts` : 'User Contacts'}
                  </h1>
                  <p className="text-sm text-gray-600">
                    {userInfo?.email} • {contacts.length} contacts
                  </p>
                </div>
              </div>

              {/* Export */}
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={() => setShowExportMenu(prev => !prev)}
                  disabled={isExporting || contacts.length === 0}
                  aria-haspopup="menu"
                  aria-expanded={showExportMenu}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isExporting ? (
                    <>
                      <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Exporting…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Export Contacts
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>

                {showExportMenu && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20"
                  >
                    <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                      All contacts ({contacts.length})
                    </p>
                    <button
                      role="menuitem"
                      onClick={() => handleExport('CSV', 'all')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Download as CSV
                    </button>
                    <button
                      role="menuitem"
                      onClick={() => handleExport('Excel', 'all')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Download as Excel (.xlsx)
                    </button>

                    {/* Only offer the filtered scope when it differs from everything */}
                    {sortedContacts.length !== contacts.length && (
                      <>
                        <div className="my-1 border-t border-gray-100" />
                        <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                          Current results ({sortedContacts.length})
                        </p>
                        <button
                          role="menuitem"
                          onClick={() => handleExport('CSV', 'filtered')}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Download as CSV
                        </button>
                        <button
                          role="menuitem"
                          onClick={() => handleExport('Excel', 'filtered')}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Download as Excel (.xlsx)
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search Contacts
              </label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or company..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="subscribed">Subscribed</option>
                <option value="unsubscribed">Unsubscribed</option>
                <option value="pending">Pending</option>
                <option value="bounced">Bounced</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-2">
                Sort By
              </label>
              <div className="flex space-x-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'email' | 'createdAt')}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="createdAt">Date Added</option>
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Contacts List */}
        <div className="bg-white rounded-lg shadow">
          {sortedContacts.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No contacts found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'This user hasn\'t added any contacts yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Added
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Activity
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedContacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-blue-600">
                                  {(contact.firstName?.[0] || contact.email[0]).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {contact.firstName && contact.lastName
                                  ? `${contact.firstName} ${contact.lastName}`
                                  : contact.firstName || contact.lastName || 'No name'
                                }
                              </div>
                              {contact.phone && (
                                <div className="text-sm text-gray-500">{contact.phone}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{contact.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{contact.company || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(contact.status)}`}>
                            {contact.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(contact.addedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {contact.lastChanged ? formatDate(contact.lastChanged) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                      Showing {indexOfFirstContact + 1} to {Math.min(indexOfLastContact, sortedContacts.length)} of{' '}
                      {sortedContacts.length} contacts
                    </span>
                    <label className="flex items-center gap-2 text-sm text-gray-500 whitespace-nowrap">
                      Rows per page
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        {PAGE_SIZE_OPTIONS.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${currentPage === 1
                          ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        Previous
                      </button>

                      <div className="flex items-center space-x-1">
                        {visiblePages.map((page, index, array) => {
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;
                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && <span className="px-2 text-gray-500">...</span>}
                              <button
                                onClick={() => setCurrentPage(page)}
                                aria-current={currentPage === page ? 'page' : undefined}
                                className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${currentPage === page
                                  ? 'bg-cyan-500 border-cyan-500 text-white'
                                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                  }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${currentPage === totalPages
                          ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        {sortedContacts.length > 0 && (searchTerm || statusFilter !== 'all') && (
          <div className="mt-4 text-sm text-gray-600">
            Filtered to {sortedContacts.length} of {contacts.length} contacts
            {searchTerm && ` matching "${searchTerm}"`}
            {statusFilter !== 'all' && ` with status "${statusFilter}"`}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserContacts;
